/**
 * HrRotation — Phase 4.C
 *
 * Time series of HR competition_id rotations per sport per day, plus
 * a longevity histogram (how far ahead of game-time HR posts new
 * bet IDs) and a recent-rotations table.
 *
 * Source: R2:_hr_bet_ids/_meta.json — the operator's existing HR
 * comp_id index, populated by the VPS scraper. Worker endpoint
 * /api/hr-rotation reads it and computes the aggregations.
 */
import { html, type TemplateResult } from "lit-html";
import * as echarts from "echarts";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { store } from "../data/store";
import type { HrRotation, HrRotationDay, HrCompetition } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

const SPORT_COLORS: Record<string, string> = {
  NBA:    "#ffb43d",
  WNBA:   "#2dd4bf",
  MLB:    "#5fa9ff",
  NHL:    "#d39bff",
  SOCCER: "#5fd47f",
  UFC:    "#ff6b6b",
  TENNIS: "#fbbf24",
  OTHER:  "#a1a1a6",
};

interface ChartHandle { inst: any; el: HTMLElement | null; }
const charts = {
  trend:     { inst: null, el: null } as ChartHandle,
  longevity: { inst: null, el: null } as ChartHandle,
};
let lastDataKey = "";

export function renderHrRotation(_onChange: () => void): TemplateResult {
  const e = store.get<HrRotation>("hrRotation");
  if (e.error)              return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data)              return emptyView();

  const r = e.data;
  if ((r as any).error === "no_meta") return notReadyView();

  const dataKey = `${r.total_competitions}/${r.daily_series.length}/${r.bet_id_files}`;
  queueMicrotask(() => mountCharts(r, dataKey));

  return html`
    <div class="hr-rot">
      ${heroStrip(r)}

      <section class="panel hr-rot-card">
        <header class="hr-rot-card__head">
          <div class="hr-rot-card__heading">
            <h3>Rotation rate · per-sport per-day</h3>
            <span class="hr-rot-card__sub">
              new comp_ids first captured each day, by sport
            </span>
          </div>
          ${renderExplainButton({
            label: "Explain rotation pattern",
            context: () => buildTrendExplainContext(r),
          })}
        </header>
        <div id="hr-rot-trend" class="hr-rot-canvas"></div>
        <div class="hr-rot-legend">
          ${r.sports_seen.map((s) => html`
            <span class="hr-rot-legend__item">
              <span class="hr-rot-legend__swatch"
                    style="background:${SPORT_COLORS[s] ?? SPORT_COLORS.OTHER};"></span>
              <span>${s}</span>
            </span>
          `)}
        </div>
      </section>

      <div class="hr-rot-grid">
        <section class="panel hr-rot-card">
          <header class="hr-rot-card__head">
            <div class="hr-rot-card__heading">
              <h3>bet_id longevity</h3>
              <span class="hr-rot-card__sub">
                hours from first capture to game time
              </span>
            </div>
          </header>
          <div id="hr-rot-longevity" class="hr-rot-canvas"
               style="height:240px;"></div>
        </section>

        <section class="panel hr-rot-card">
          <header class="hr-rot-card__head">
            <div class="hr-rot-card__heading">
              <h3>Recent rotations</h3>
              <span class="hr-rot-card__sub">
                ${r.recent_rotations.length} most-recent comp_id captures
              </span>
            </div>
          </header>
          <div class="hr-rot-recent">
            ${r.recent_rotations.slice(0, 12).map(recentRow)}
          </div>
        </section>
      </div>
    </div>
  `;
}

// =============================================================
// Hero strip
// =============================================================
function heroStrip(r: HrRotation): TemplateResult {
  // 7-day rolling rate
  const last7 = r.daily_series.slice(-7);
  const sum7 = last7.reduce((s, d) => s + d.total, 0);
  const avg7 = last7.length > 0 ? sum7 / last7.length : 0;

  // Span days
  const span = r.daily_series.length > 1
    ? Math.round((Date.parse(r.daily_series[r.daily_series.length - 1]!.date)
       - Date.parse(r.daily_series[0]!.date)) / 86400_000) + 1
    : r.daily_series.length;

  return html`
    <section class="panel hr-rot-hero">
      <div class="hr-rot-hero__row">
        <div class="hr-rot-hero__metric">
          <span class="hr-rot-hero__label">total comp_ids</span>
          <span class="hr-rot-hero__value">${r.total_competitions}</span>
          <span class="hr-rot-hero__sub">${r.bet_id_files} bet-id files stashed</span>
        </div>
        <div class="hr-rot-hero__metric">
          <span class="hr-rot-hero__label">history span</span>
          <span class="hr-rot-hero__value">${span}d</span>
          <span class="hr-rot-hero__sub">${r.daily_series.length} active days</span>
        </div>
        <div class="hr-rot-hero__metric">
          <span class="hr-rot-hero__label">7d avg rotation</span>
          <span class="hr-rot-hero__value">${avg7.toFixed(1)}/d</span>
          <span class="hr-rot-hero__sub">${sum7} new comps in last 7d</span>
        </div>
        <div class="hr-rot-hero__metric">
          <span class="hr-rot-hero__label">sports tracked</span>
          <span class="hr-rot-hero__value">${r.sports_seen.length}</span>
          <span class="hr-rot-hero__sub">${r.sports_seen.join(" · ")}</span>
        </div>
        <span class="hr-rot-hero__spacer"></span>
        ${renderExplainButton({
          label: "Explain rotation state",
          context: () => buildOverallExplainContext(r),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Recent table row
// =============================================================
function recentRow(c: HrCompetition): TemplateResult {
  const color = SPORT_COLORS[c.sport] ?? SPORT_COLORS.OTHER;
  const cap = c.captured_at
    ? new Date(c.captured_at).toLocaleString([], {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";
  const lead = c.lead_hours == null ? "—" : `${c.lead_hours.toFixed(1)}h ahead`;
  return html`
    <div class="hr-rot-recent__row">
      <span class="hr-rot-recent__sport"
             style="color:${color};">${c.sport}</span>
      <span class="hr-rot-recent__name">${c.name || c.compName}</span>
      <span class="hr-rot-recent__cap">${cap}</span>
      <span class="hr-rot-recent__lead">${lead}</span>
    </div>
  `;
}

// =============================================================
// Charts
// =============================================================
function mountCharts(r: HrRotation, dataKey: string): void {
  const newTrend     = document.getElementById("hr-rot-trend");
  const newLongevity = document.getElementById("hr-rot-longevity");
  if (!newTrend || !newLongevity) return;

  // Tab-switch protection: dispose if container changed
  const trendStale     = charts.trend.el     && charts.trend.el     !== newTrend;
  const longevityStale = charts.longevity.el && charts.longevity.el !== newLongevity;
  if (trendStale)     { charts.trend.inst?.destroy?.();   charts.trend.inst = null; }
  if (longevityStale) { charts.longevity.inst?.dispose(); charts.longevity.inst = null; }
  if (trendStale || longevityStale) lastDataKey = "";

  charts.trend.el     = newTrend;
  charts.longevity.el = newLongevity;

  if (dataKey === lastDataKey) return;
  lastDataKey = dataKey;

  // ---- uPlot multi-series trend ----
  if (charts.trend.inst) charts.trend.inst.destroy();
  const series = trendSeries(r);
  charts.trend.inst = new uPlot(
    {
      width:  charts.trend.el.clientWidth,
      height: 260,
      cursor: { drag: { x: false, y: false } },
      legend: { show: false },
      scales: { x: { time: true } },
      axes: [
        {
          stroke: "#6e6e73", grid: { stroke: "#1c1c1e" },
          values: (_self, ticks) => ticks.map((t) =>
            new Date(t * 1000).toLocaleDateString([], { month: "short", day: "numeric" })),
        },
        {
          stroke: "#6e6e73", grid: { stroke: "#1c1c1e" },
          values: (_self, ticks) => ticks.map((t) => `${Math.round(t)}`),
        },
      ],
      series: series.uplotSeries,
    },
    series.data,
    charts.trend.el,
  );

  // ---- ECharts longevity histogram ----
  if (!charts.longevity.inst) charts.longevity.inst = echarts.init(charts.longevity.el, "dark");
  charts.longevity.inst.setOption(longevityOption(r), true);

  if (!(window as any).__hrRotResize) {
    (window as any).__hrRotResize = true;
    window.addEventListener("resize", () => {
      if (charts.trend.el) {
        charts.trend.inst?.setSize({ width: charts.trend.el.clientWidth, height: 260 });
      }
      charts.longevity.inst?.resize();
    });
  }
}

function trendSeries(r: HrRotation): {
  uplotSeries: any[];
  data: any[];
} {
  const xs = r.daily_series.map((d: HrRotationDay) => Date.parse(d.date) / 1000);
  const sportSeries: any[] = [{ label: "date" }];
  const sportData: number[][] = [];

  for (const sport of r.sports_seen) {
    const ys = r.daily_series.map((d) => d.per_sport[sport] ?? 0);
    sportData.push(ys);
    sportSeries.push({
      label:  sport,
      stroke: SPORT_COLORS[sport] ?? SPORT_COLORS.OTHER,
      width:  2,
      points: { show: true, size: 5,
                 fill: SPORT_COLORS[sport] ?? SPORT_COLORS.OTHER },
    });
  }

  return {
    uplotSeries: sportSeries,
    data: [xs, ...sportData],
  };
}

function longevityOption(r: HrRotation): echarts.EChartsOption {
  const order = ["<1h", "1-6h", "6-24h", "1-3d", "3-7d", "7d+", "unknown"];
  const colors = ["#ff6b6b", "#ff9f0a", "#fbbf24", "#5fd47f", "#5fa9ff", "#a5a3f0", "#3a3a3c"];
  const cats = order.filter((k) => k in r.longevity_buckets);
  const data = cats.map((k, i) => ({
    value: r.longevity_buckets[k] ?? 0,
    itemStyle: { color: colors[i] ?? "#6e6e73" },
  }));
  return {
    backgroundColor: "transparent",
    grid: { left: 50, right: 30, top: 30, bottom: 50, containLabel: true },
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(20, 20, 22, 0.96)",
      borderColor: "rgba(255,255,255,0.10)",
      textStyle: { color: "#e6e6eb", fontSize: 12 },
    },
    xAxis: {
      type: "category", data: cats,
      axisLabel: { color: "#a1a1a6", fontSize: 11, fontWeight: "bold" as const },
      axisLine: { lineStyle: { color: "#2c2c2e" } },
    },
    yAxis: {
      type: "value", name: "comps",
      nameTextStyle: { color: "#6e6e73", fontSize: 10 },
      axisLabel: { color: "#6e6e73", fontSize: 10 },
      splitLine: { lineStyle: { color: "#1c1c1e" } },
    },
    series: [{
      name: "comp_ids",
      type: "bar",
      data,
      barWidth: "62%",
      label: {
        show: true, position: "top" as const,
        color: "#a1a1a6", fontSize: 10,
      },
    } as echarts.BarSeriesOption],
  } as echarts.EChartsOption;
}

// =============================================================
// Explain context builders
// =============================================================
function buildOverallExplainContext(r: HrRotation): string {
  const last7 = r.daily_series.slice(-7);
  return [
    `HR rotation state:`,
    `Total comp_ids known: ${r.total_competitions}`,
    `Sports tracked: ${r.sports_seen.join(", ")}`,
    `Bet-id files stashed: ${r.bet_id_files}`,
    `History span: ${r.daily_series.length} days`,
    "",
    "Last 7 days new-comp counts:",
    ...last7.map((d) =>
      `  ${d.date}: total=${d.total}, ` +
      Object.entries(d.per_sport).filter(([, n]) => n > 0).map(([s, n]) => `${s}=${n}`).join(", "),
    ),
    "",
    `Longevity histogram (lead time from capture to game-time):`,
    ...Object.entries(r.longevity_buckets).map(([k, n]) => `  ${k}: ${n}`),
  ].join("\n");
}

function buildTrendExplainContext(r: HrRotation): string {
  return [
    "HR rotation rate per-sport per-day:",
    ...r.daily_series.slice(-14).map((d) =>
      `  ${d.date}: ` + Object.entries(d.per_sport).filter(([, n]) => n > 0)
        .map(([s, n]) => `${s}=${n}`).join(", ") || "(none)",
    ),
  ].join("\n");
}

// =============================================================
// State views
// =============================================================
function notReadyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:520px;margin:0 auto;">
        <p style="font-size:15px;color:var(--text-primary);margin:0 0 8px;">
          No HR meta yet
        </p>
        <p style="margin:0;">
          The Rotation tracker reads
          <code style="color:var(--accent-blue-light);">_hr_bet_ids/_meta.json</code>
          on R2. The VPS scraper writes it; if this view is empty, the
          scraper hasn't published any meta yet or the bucket is missing
          the path.
        </p>
      </div>
    </div>
  `;
}

function skeletonView(): TemplateResult {
  return html`
    <div class="hr-rot">
      <section class="panel" style="height:100px;"></section>
      <section class="panel" style="height:300px;"></section>
      <div class="hr-rot-grid">
        <section class="panel" style="height:280px;"></section>
        <section class="panel" style="height:280px;"></section>
      </div>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);">No rotation data.</div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>HR Rotation</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
